/**
 * Typed layer model for the HTML ad editor. The previous architecture stored
 * state as an HTMLElement tree (parsed via DOMParser) and sync'd to the live
 * rendered DOM via dangerouslySetInnerHTML; drag/resize bypassed React with
 * direct DOM mutation. That dual-tree design caused stale-overlay-vs-content
 * bugs and made every interaction depend on freeze/thaw gating.
 *
 * The new model is plain data: a `Doc` with a typed `Layer[]` tree. The canvas
 * is a pure render of `Doc`. Edits dispatch typed updates. Serialization is a
 * boundary concern (load from HTML, save to HTML).
 */

import type { AnimationBlock } from "./animation-blocks";
import type { TextSegment } from "./text-segments";

export interface GradientStop {
  /** 0..100 */
  offset: number;
  color: string;
}

export type FillKind = "solid" | "linear" | "radial";

export interface Fill {
  kind: FillKind;
  /** Used when kind === "solid". */
  color: string;
  /** Degrees, 0..360. Used when kind === "linear". */
  angle: number;
  /** Used when kind is "linear" or "radial". */
  stops: GradientStop[];
}

export interface BorderSide {
  /** 0 means no border on this side. */
  width: number;
  color: string;
}

/**
 * Per-side border. Most ads have uniform borders (all four sides equal);
 * a few (CSS-triangle callout tails, asymmetric outlines) need per-side
 * values, which round-trip through the editor as individual longhands.
 * `null` on `Border` (in `HtmlStyle.border`) means no border at all.
 */
export interface Border {
  top: BorderSide;
  right: BorderSide;
  bottom: BorderSide;
  left: BorderSide;
}

export function uniformBorder(width: number, color: string): Border {
  // Each side is its own `BorderSide` object — sharing one reference across
  // all four sides is a mutation footgun (an edit to `border.top.width` would
  // stomp the other three).
  return {
    top: { width, color },
    right: { width, color },
    bottom: { width, color },
    left: { width, color },
  };
}

export function isUniformBorder(border: Border): boolean {
  const { top, right, bottom, left } = border;
  return (
    top.width === right.width &&
    right.width === bottom.width &&
    bottom.width === left.width &&
    top.color === right.color &&
    right.color === bottom.color &&
    bottom.color === left.color
  );
}

export interface Shadow {
  x: number;
  y: number;
  blur: number;
  spread: number;
  color: string;
}

/**
 * One layer of a CSS `text-shadow` — the glyph shadow, distinct from the box
 * `shadow` above. `text-shadow` has no spread, so it's `Shadow` minus that
 * field. `text-shadow` is a comma-separated *list* of these (see
 * `TextLayer.textShadow`), so a single value like Figma's layered effects
 * survives round-trips.
 */
export type TextShadow = Omit<Shadow, "spread">;

export type TextAlign = "left" | "center" | "right" | "justify";
export type ObjectFit = "cover" | "contain" | "fill" | "none";

/**
 * Figma-style text resizing mode, inferred by the flatten from the source's
 * CSS and carried in the emitted geometry:
 * - `auto-width` hugs one line (`width:max-content; white-space:nowrap`)
 * - `auto-height` wraps at the authored width and hugs vertically (no pinned
 *   `height`)
 * - `fixed` pins width and height; vertical alignment places text inside it
 * Absent on legacy layers, whose box is pinned width+height.
 */
export type TextResizing = "auto-width" | "auto-height" | "fixed";

/**
 * Grow anchor for a template slot's hugged axis, emitted by the flatten as a
 * translate so per-product substitution keeps the design's alignment with
 * pure CSS: `center`/`middle` is `translateX/Y(-50%)`, `right`/`bottom` is
 * `translateX/Y(-100%)`. When set, the layer's `x`/`y` is the anchor
 * coordinate (the CSS `left`/`top` value, verbatim), not the visual top-left
 * edge — dragging still moves it 1:1, and overlay math derives the visual box
 * from the anchor plus the measured size.
 */
export type TextAnchorX = "center" | "right";
export type TextAnchorY = "middle" | "bottom";
export type TextVerticalAlign = "top" | "center" | "bottom";

/**
 * One legacy (composition `version: 1`) animation applied to a layer of a
 * video overlay. `component` names an entry in the Remotion animation
 * registry (`@ad-studio/anim/fade`, …), `startFrame` is relative to the
 * overlay's own window in the composition, and `props` are per-animation
 * (duration, direction, feel, …).
 *
 * Retained for `animated-svg` layers, which store descriptors, and for the
 * v1 to v2 backfill that converts them to `AnimationBlock`s; html-overlay
 * layers store blocks.
 * Defined here rather than in `ad-studio-renderer` because lib deliberately
 * holds no React dependency; the renderer re-exports this type.
 */
export interface AnimationDescriptor {
  component: string;
  startFrame: number;
  props: Record<string, unknown>;
  /**
   * Optional id inside an opaque SVG layer. SVG constructs the HTML editor
   * cannot decompose are preserved as one SvgLayer; legacy sidecar motion can
   * still address a descendant by carrying its original SVG id here.
   */
  targetId?: string;
}

/**
 * Motion for a layer of a video overlay, serialized as the `data-ht-motion`
 * attribute. Present only on video-overlay Docs; static ads never carry it.
 * The composition sidecar carries the same blocks keyed by layer id and is
 * what the renderer reads; the attribute is the editor's mirror of it.
 */
export interface LayerMotion {
  blocks: AnimationBlock[];
}

interface BaseLayer {
  id: string;
  /**
   * Template slot id (the `data-slot` attribute). Present on layers that came
   * from an Enriched Catalog template — per-product rendering substitutes
   * this layer's content/src by slot id. Round-trips opaquely: the editor
   * never invents or removes slot ids.
   */
  slotId?: string;
  /**
   * Semantic layer tag (the `data-layer-type` attribute) stamped by upstream
   * pipelines — e.g. the LCM decomposer marks text layers with
   * `data-layer-type="text"`, which the HTML→SVG serializer relies on to emit
   * them as text. Round-trips opaquely: the editor never invents or removes it.
   */
  layerType?: string;
  /** Position (px) relative to parent (canvas root or group). */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Degrees, 0..360. */
  rotation: number;
  /**
   * Mirror about the layer's own center (CSS `scaleX(-1)`/`scaleY(-1)`,
   * applied after rotation). The box geometry is unchanged — only the
   * content mirrors.
   */
  flipX?: boolean;
  flipY?: boolean;
  /** 0..1. */
  opacity: number;
  hidden: boolean;
  locked: boolean;
  /** Optional human label override; otherwise derived from type/content. */
  label?: string;
  /**
   * Motion, present only when this layer belongs to a video overlay. See
   * {@link LayerMotion}.
   */
  motion?: LayerMotion;
  /**
   * Inline-style declarations the editor doesn't model (display:flex, padding,
   * filter, text-transform, white-space, ...). Captured at parse time so they
   * round-trip through serialize and the renderer applies them alongside the
   * modeled props. Property names are kebab-case (CSS form). Modeled props
   * always win on conflict — they're applied after extraStyles.
   */
  extraStyles?: Record<string, string>;
}

/**
 * One corner's radius in px. `number` is the circular case (rx = ry = N) —
 * the editor's per-corner slider produces these. `{ rx, ry }` carries the
 * elliptical axes that survive a parse from `border-radius: 50%` on a
 * non-square box (or explicit `Hpx/Vpx` shorthand). The renderer emits these
 * via the slash form of `border-radius`; user edits in the UI collapse back
 * to `number` (the UI exposes one slider per corner).
 */
export type CornerRadius = number | { rx: number; ry: number };

/**
 * Per-corner border-radius in px. Bare `number` is the uniform circular case
 * (all four corners equal AND circular). Object form addresses each corner
 * independently and each corner may be elliptical.
 */
export type BorderRadius =
  | number
  | { tl: CornerRadius; tr: CornerRadius; br: CornerRadius; bl: CornerRadius };

/** Properties shared by HTML-rendered layers (rect/text/image). */
interface HtmlStyle {
  fill: Fill | null;
  border: Border | null;
  /**
   * Stacked CSS `box-shadow` layers in CSS source order (first paints on
   * top). Empty = no shadow. A list — not a single value — so multi-layer
   * effects round-trip and stay editable, like `textShadow`.
   */
  shadow: Shadow[];
  borderRadius: BorderRadius;
}

export interface RectLayer extends BaseLayer, HtmlStyle {
  type: "rect";
}

export interface TextLayer extends BaseLayer, HtmlStyle {
  type: "text";
  /**
   * Stacked CSS `text-shadow` layers on the glyphs (distinct from the box
   * `shadow`), in paint order. Empty = no text shadow. A list — not a single
   * value — so multi-layer effects round-trip and stay editable.
   */
  textShadow: TextShadow[];
  /**
   * Resizing mode for the hugged axes. When set, `width` (auto-width) and/or
   * `height` (both auto modes) are a *measurement* of the hugged box — the
   * editor backfills them after render for selection overlays — and are not
   * serialized for the hugged axes.
   */
  resizing?: TextResizing;
  /** Grow anchor for an auto-width slot; absent = left edge. */
  anchorX?: TextAnchorX;
  /** Grow anchor for an auto-height slot; absent = top edge. */
  anchorY?: TextAnchorY;
  /**
   * Truncate to N lines with an ellipsis — Figma's "max lines". Pairs with
   * `auto-height` (a cap on growth): the box hugs the clamped content, so
   * long values cut at a line boundary with an ellipsis, never sliced
   * mid-glyph by a pinned height. Round-trips as `display:-webkit-box;
   * -webkit-box-orient:vertical; -webkit-line-clamp:N; overflow:hidden`.
   */
  maxLines?: number;
  /** Sanitized inline HTML — supports <br>, <span>, <strong>, etc. */
  content: string;
  /**
   * Canonical first font name. The full CSS font-family value (with fallback
   * chain) is derived from this at render/serialize time via
   * `fontFamilyStackFor` — no separate stack field so there's only one source
   * of truth for the dropdown to mutate.
   */
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  color: string;
  textAlign: TextAlign;
  /** Alignment inside a fixed-height text box; absent means top. */
  verticalAlign?: TextVerticalAlign;
  /** Raw CSS value, e.g. "1.2" or "24px". Empty string = default. */
  lineHeight: string;
  /** Raw CSS value, e.g. "0.5px". Empty string = default. */
  letterSpacing: string;
  italic: boolean;
  underline: boolean;
  strikethrough?: boolean;
  /**
   * Figma family name for this layer, resolved against the ad's brand
   * fonts: the true typographic family of the file `fontFamily` references
   * ("Mulish" for a layer on the `Mulish-VF_` upload alias), or the CSS
   * value itself for families outside the brand catalog. Populated by the
   * Figma-export pipeline (`figma-font.ts`); undefined in the
   * editor's own state.
   */
  figmaFontFamily?: string;
  /**
   * Style name (e.g. "Bold", "Bold Italic", "Regular") chosen from the
   * layer's own CSS `fontWeight` / `italic` — a variable font resolves to
   * the named instance nearest the layer's weight. The plugin still
   * synthesizes faux bold/italic when the loaded face provides less than
   * the CSS asks for.
   */
  figmaFontStyle?: string;
  /**
   * Ordered fallbacks when the primary isn't registered in Figma: spelling
   * variants plus the legacy filename-parsed pair, tried in order.
   */
  figmaFontAlternatives?: { family: string; style: string }[];
  /** Populated by Figma-export; undefined in editor state. See `text-segments.ts`. */
  figmaTextSegments?: TextSegment[];
}

/**
 * A first-class image crop (Figma-style). When present, the layer's
 * `x/y/width/height` is the visible **frame** (the crop window on the canvas,
 * which clips), and `crop` is the source image's draw-box *relative to the
 * frame's top-left*. The draw-box keeps the image's natural aspect ratio, so
 * the image is never distorted: panning moves `(x, y)` and zooming scales
 * `(width, height)` in lockstep. This mirrors the structure decomposed bases
 * already use — an `overflow:hidden` frame wrapping an oversized, offset
 * `<img>` — which `parse.ts` collapses into this single layer.
 */
export interface ImageCrop {
  /** Image draw-box offset from the frame's top-left, px (≤ 0 when zoomed in). */
  x: number;
  y: number;
  /** Image draw-box size, px. `width:height` keeps the source's aspect ratio. */
  width: number;
  height: number;
}

export interface ImageLayer extends BaseLayer, HtmlStyle {
  type: "image";
  src: string;
  objectFit: ObjectFit;
  /**
   * Crop transform: the image's draw-box within the frame (`x/y/width/height`).
   * Absent for an uncropped image (rendered as a plain `<img>` + `objectFit`).
   */
  crop?: ImageCrop;
  /**
   * The original `ad:filename` reference, if `src` was resolved from one.
   * `resolveAdRefs` records this via the `data-ad-ref` attribute so the
   * editor can save back to the portable ref form instead of baking in a
   * region-specific URL. Anything that swaps `src` must set this alongside it.
   */
  adRef?: string;
  /**
   * S3 key recovered from the `?key=` param of the resolved
   * `/agents/files?key=…` URL at parse time. Null for external URLs or any
   * src we can't parse. The key's path prefix indicates provenance — see
   * {@link getImageOrigin}.
   */
  assetKey: string | null;
}

export type ImageOrigin = "asset" | "generated" | "unknown";

/**
 * Classify an image layer by its S3 key. Filename prefix is the strongest
 * signal — a component asset lands under the `ad-assets/` path whatever
 * produced it, so only the name separates them: AI-generated bases carry a
 * `generated-` prefix, layerize cutouts a `layer-z<NN>-` prefix, and
 * user-uploaded sub-assets extracted from agent SVGs are prefixed
 * `user_asset_`. Otherwise fall back to the path bucket: `/content_nodes/` is
 * generated; `/ad-assets/` is a user upload.
 */
export function getImageOrigin(layer: ImageLayer): ImageOrigin {
  const key = layer.assetKey;
  if (!key) return "unknown";
  const filename = key.slice(key.lastIndexOf("/") + 1);
  if (filename.startsWith("generated-")) return "generated";
  // Prefix written by `layerFilename` in the image engine's decompose-image.
  // Same test as that service's `classifyImage`, mirrored rather than imported
  // because bedrock-agent-service deliberately takes no `@hightouch/*` deps.
  if (/^layer-z\d/i.test(filename)) return "generated";
  if (filename.startsWith("user_asset_")) return "asset";
  if (key.includes("/content_nodes/")) return "generated";
  if (key.includes("/ad-assets/")) return "asset";
  return "unknown";
}

/**
 * Top-level <svg> container. Inner SVG primitives are not edited individually —
 * we treat the markup as opaque and resize by writing the wrapper's
 * width/height/viewBox.
 */
export interface SvgLayer extends BaseLayer {
  type: "svg";
  innerMarkup: string;
  viewBox: string;
}

export interface GroupLayer extends BaseLayer {
  type: "group";
  children: Layer[];
}

export type Layer = RectLayer | TextLayer | ImageLayer | SvgLayer | GroupLayer;

export type LayerType = Layer["type"];

/** Layers that have HtmlStyle (fill/border/shadow/borderRadius). */
export type StyledLayer = RectLayer | TextLayer | ImageLayer;

export interface Doc {
  width: number;
  height: number;
  /** Canvas root background; null = transparent. */
  background: string | null;
  layers: Layer[];
}

export const DEFAULT_FILL: Fill = {
  kind: "solid",
  color: "#ffffff",
  angle: 0,
  stops: [],
};

export const DEFAULT_GRADIENT_STOPS: GradientStop[] = [
  { color: "#5b8def", offset: 0 },
  { color: "#b16cea", offset: 100 },
];

export function defaultStops(): GradientStop[] {
  return DEFAULT_GRADIENT_STOPS.map((s) => ({ ...s }));
}

export function isStyledLayer(layer: Layer): layer is StyledLayer {
  return (
    layer.type === "rect" || layer.type === "text" || layer.type === "image"
  );
}

/** Expand a uniform/per-corner radius to its four corner values. */
export function corners(r: BorderRadius): {
  tl: CornerRadius;
  tr: CornerRadius;
  br: CornerRadius;
  bl: CornerRadius;
} {
  return typeof r === "number" ? { tl: r, tr: r, br: r, bl: r } : r;
}

/** [rx, ry] tuple for a corner — circular case duplicates the single value. */
function cornerAxes(c: CornerRadius): [number, number] {
  return typeof c === "number" ? [c, c] : [c.rx, c.ry];
}

/** True when this corner is a perfect circle (rx === ry). */
function isCircularCorner(c: CornerRadius): boolean {
  return typeof c === "number" || c.rx === c.ry;
}

/** Single px value for UI / sort comparisons — max of rx/ry. */
export function cornerMagnitude(c: CornerRadius): number {
  return typeof c === "number" ? c : Math.max(c.rx, c.ry);
}

/** True when all four corners are circular AND equal (i.e. CSS `border-radius: Npx`). */
export function isUniformRadius(r: BorderRadius): boolean {
  if (typeof r === "number") return true;
  if (!isCircularCorner(r.tl)) return false;
  const value = cornerAxes(r.tl)[0];
  for (const c of [r.tr, r.br, r.bl]) {
    if (!isCircularCorner(c) || cornerAxes(c)[0] !== value) return false;
  }
  return true;
}

/** Collapse to bare `number` if all corners circular and equal; else return the object. */
export function normalizeRadius(r: BorderRadius): BorderRadius {
  if (typeof r === "number") return r;
  return isUniformRadius(r) ? cornerAxes(r.tl)[0] : r;
}

/** True when the radius is effectively zero (no rounded corners anywhere). */
export function hasRadius(r: BorderRadius): boolean {
  if (typeof r === "number") return r > 0;
  return (
    cornerMagnitude(r.tl) > 0 ||
    cornerMagnitude(r.tr) > 0 ||
    cornerMagnitude(r.br) > 0 ||
    cornerMagnitude(r.bl) > 0
  );
}

/**
 * Render a `BorderRadius` as the shortest valid CSS `border-radius` value.
 * Uniform circular → `Npx`. Per-corner all-circular → 4-value shorthand.
 * Any elliptical corner → `H1 H2 H3 H4 / V1 V2 V3 V4` slash form.
 */
export function borderRadiusToCss(r: BorderRadius): string {
  if (typeof r === "number") return `${r}px`;
  if (isUniformRadius(r)) return `${cornerAxes(r.tl)[0]}px`;
  const corners4: CornerRadius[] = [r.tl, r.tr, r.br, r.bl];
  if (corners4.every(isCircularCorner)) {
    const ns = corners4.map((c) => (typeof c === "number" ? c : c.rx));
    return `${ns[0]}px ${ns[1]}px ${ns[2]}px ${ns[3]}px`;
  }
  const hs = corners4.map((c) => cornerAxes(c)[0]);
  const vs = corners4.map((c) => cornerAxes(c)[1]);
  return (
    `${hs[0]}px ${hs[1]}px ${hs[2]}px ${hs[3]}px / ` +
    `${vs[0]}px ${vs[1]}px ${vs[2]}px ${vs[3]}px`
  );
}

export function canHaveChildren(layer: Layer): layer is GroupLayer {
  return layer.type === "group";
}

/**
 * An auto-layout frame: a group whose children are laid out by flexbox rather
 * than absolute positioning. The flatten preserves these for templates whose
 * slots must reflow (sale price pushing the crossed-out original). The
 * `display:flex` in extraStyles IS the marker — the flatten strips `display`
 * from every other layer — so no separate model field is needed. Children of
 * a flex group carry no meaningful `x`/`y` (flex owns placement) and are not
 * draggable/position-editable in the editor.
 */
export function isFlexGroup(layer: Layer): layer is GroupLayer {
  if (layer.type !== "group") return false;
  const display = layer.extraStyles?.display;
  return display === "flex" || display === "inline-flex";
}

/** Whether a text layer's width hugs its content (not resizable/pinnable). */
export function textHugsWidth(layer: Layer): boolean {
  return layer.type === "text" && layer.resizing === "auto-width";
}

/** Whether a text layer's height hugs its content (not resizable/pinnable). */
export function textHugsHeight(layer: Layer): boolean {
  return (
    layer.type === "text" &&
    (layer.resizing === "auto-width" || layer.resizing === "auto-height")
  );
}

/**
 * Whether the layer's height hugs its content on *any* layer kind: auto-sized
 * text, or a flex frame while unpinned (height 0 is its hug convention).
 */
export function hugsHeightAxis(layer: Layer): boolean {
  return textHugsHeight(layer) || (isFlexGroup(layer) && layer.height === 0);
}

/*
 * `supportsWidth`/`supportsHeight` answer "does this layer's model number for
 * the axis reach the render?", which is a fact about the emitted CSS, not
 * about any one consumer. They live here, next to the hug predicates and in
 * the same module as the `TextResizing` type, so the serializer, the editor's
 * live renderer and the properties panel cannot drift apart on it —
 * `serialize.ts` emits `width:max-content` for auto-width text and no `height`
 * at all for a hugged one, so a width or height written to such a layer is
 * stored and then ignored. `model.test.ts` pins that against real serializer
 * output, so a format change fails here rather than quietly turning the
 * properties panel into a liar.
 */

/** Whether the layer renders its model `width` (hugged axes do not). */
export function supportsWidth(layer: Layer): boolean {
  return !textHugsWidth(layer);
}

/** Whether the layer renders its model `height` (hugged axes do not). */
export function supportsHeight(layer: Layer): boolean {
  return !hugsHeightAxis(layer);
}

/**
 * Auto-name that upstream pipelines stamp onto image layers via `data-label`.
 * It isn't a meaningful identity — every base image ends up called "Generated"
 * — so we treat it as absent and fall through to the type default ("Image").
 * A real user rename (any other value) is always honored.
 */
const NON_MEANINGFUL_IMAGE_LABEL = "Generated";

export function getLayerLabel(layer: Layer): string {
  if (
    layer.label &&
    !(layer.type === "image" && layer.label === NON_MEANINGFUL_IMAGE_LABEL)
  ) {
    return layer.label;
  }
  if (layer.type === "text") {
    const text = layer.content.replace(/<[^>]+>/g, "").trim();
    if (text) {
      const TRUNCATE = 24;
      return text.length > TRUNCATE ? `${text.slice(0, TRUNCATE)}…` : text;
    }
    return "Text";
  }
  if (layer.type === "image") return "Image";
  if (layer.type === "svg") return "SVG";
  if (layer.type === "group") return "Group";
  return "Rectangle";
}
